import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";

interface RedactedValueProps {
  value: string | null;
  /** Placeholder shown when value is null or empty. Defaults to "-". */
  emptyPlaceholder?: string;
  /** Additional CSS classes for the wrapper element. */
  className?: string;
}

/**
 * Displays a redacted (secret) value as masked bullets by default.
 * The user can reveal the actual content by pressing the eye button.
 */
const RedactedValue = ({ value, emptyPlaceholder = "-", className }: RedactedValueProps) => {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  if (!value) {
    return <span className={className}>{emptyPlaceholder}</span>;
  }

  return (
    <span className={`inline-flex items-center gap-1 ${className ?? ""}`}>
      <span className={visible ? "font-mono" : "font-mono select-none"}>
        {visible
          ? value.split(",").map((s) => s.trim()).join(" ")
          : "••••••••"}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 text-muted-foreground hover:text-foreground"
        aria-label={visible ? t("hideRedacted") : t("showRedacted")}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <EyeOff size={12} /> : <Eye size={12} />}
      </Button>
    </span>
  );
};

export default RedactedValue;
